import {useCallback, useState, useRef} from 'react';
import {useRecurringCallback} from 'src/v2/components/anyCrypto/deposit/useRecurringCallback';
import {useRequestRelayQuote} from 'src/v2/components/anyCrypto/useRequestRelayQuote';
import {useSelector} from 'src/v2/state/store';
import {useIsRelayWalletConnectCurrency} from 'src/v2/components/pay/useIsRelayWalletConnectCurrency';
import {isRelaySupportedCurrency} from 'src/v2/components/anyCrypto/relayConstants';
import {isStablecoin} from 'src/v2/util/isStablecoin';

type RelayQuote = {
  depositAddress: string | null;
  inputAmount: string | null;
  inputChain: string | null;
  inputCurrency: string | null;
};

export const QUOTE_REFRESH_INTERVAL_S = 60;

export const useRelayQuoteForPayment = (): {
  quote: RelayQuote;
  loading: boolean;
  error: Error | null;
  secondsUntilRefresh: number | null;
} => {
  const selectedCurrency = useSelector((state) => state.selectedCurrency);
  const quote = useSelector((state) => state.anyCryptoDeposit.quote);
  const isRelayWalletConnectCurrency = useIsRelayWalletConnectCurrency();
  const requestRelayQuote = useRequestRelayQuote();
  const requestRelayQuoteRef = useRef(requestRelayQuote);
  requestRelayQuoteRef.current = requestRelayQuote;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const relayCurrency =
    selectedCurrency && isRelaySupportedCurrency(selectedCurrency)
      ? selectedCurrency
      : null;

  const shouldSkip = !isRelayWalletConnectCurrency || !relayCurrency;

  const handleRefresh = useCallback(async () => {
    if (!relayCurrency) return;
    setLoading(true);
    setError(null);
    try {
      await requestRelayQuoteRef.current(
        relayCurrency.assetCode,
        relayCurrency.currencyNetwork,
      );
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [relayCurrency]);

  const {secondsUntilRun: secondsUntilRefresh} = useRecurringCallback(
    shouldSkip
      ? {skip: true}
      : {
          callback: handleRefresh,
          intervalSeconds: QUOTE_REFRESH_INTERVAL_S,
          runImmediately: true,
          maxRuns: isStablecoin(relayCurrency.assetCode) ? 1 : undefined,
        },
  );

  return {quote, loading, error, secondsUntilRefresh};
};
